import React, { useEffect, useState } from 'react';
import { CheckCircle, Home } from 'lucide-react';
import './Success.css';

// URL del backend de formularios. Prioridad env > fallback hardcoded.
const FORMS_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function Success() {
  const [orderId, setOrderId] = useState(null);
  const [formSent, setFormSent] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const [bookingDetails, setBookingDetails] = useState(null); // { meetLink, datetimeUTC }

  useEffect(() => {
    // 🔥 Prioridad de fuentes para el orderId:
    //  1. sessionStorage.lastConfirmedOrderId  (Confirming.js lo guardó justo antes de borrar currentPayment)
    //  2. localStorage.currentPayment.orderId  (camino Stripe síncrono que aún no pasó por Confirming)
    // Tras 85ca004, el backend es la fuente principal del Email 2. Esta llamada
    // a /api/payment-complete actúa como fallback (con guard de idempotencia en backend).
    let paymentId = null;
    try {
      const lastConfirmed = sessionStorage.getItem('lastConfirmedOrderId');
      if (lastConfirmed) {
        paymentId = lastConfirmed;
        setOrderId(lastConfirmed);
        sessionStorage.removeItem('lastConfirmedOrderId');
      }
    } catch (e) {}

    const saved = localStorage.getItem('currentPayment');
    if (!paymentId && saved) {
      try {
        const data = JSON.parse(saved);
        setOrderId(data.orderId);
        paymentId = data.orderId;
        console.log('✅ Pago completado - Order ID:', data.orderId);
      } catch (e) {
        console.error('Error parseando currentPayment:', e);
      }
    }

    // 🔥 PROCESAR RESERVA DE CALENDARIO SI EXISTE
    const pendingBooking = localStorage.getItem('pendingBooking');
    if (pendingBooking) {
      try {
        const bk = JSON.parse(pendingBooking);
        console.log('📅 Procesando reserva de calendario:', bk);
        setBookingStatus('sending');
        
        fetch(`${FORMS_BACKEND_URL}/api/calendar/book`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerEmail: bk.customerEmail,
            customerName: bk.customerName || '',
            productName: bk.productName,
            paymentId: paymentId || 'unknown',
            slotISO: bk.slotISO
          })
        })
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            console.log('✅ Reserva creada:', data);
            setBookingStatus('success');
            setBookingDetails({ meetLink: data.meetLink, datetimeUTC: data.datetimeUTC, slotLabel: bk.slotLabel });
          } else {
            console.error('❌ Error creando reserva:', data);
            setBookingStatus('error');
          }
          localStorage.removeItem('pendingBooking');
        })
        .catch(err => {
          console.error('❌ Error reserva:', err);
          setBookingStatus('error');
          localStorage.removeItem('pendingBooking');
        });
      } catch (e) {
        console.error('Error parseando pendingBooking:', e);
        localStorage.removeItem('pendingBooking');
      }
    }

    // 🔥 ENVIAR DATOS DEL FORMULARIO SI EXISTEN
    const pendingFormData = localStorage.getItem('pendingFormData');

    if (pendingFormData) {
      console.log('📋 Formulario pendiente detectado, enviando...');

      let formDataObj = JSON.parse(pendingFormData);

      // 🔥 Restaurar archivos base64 desde sessionStorage
      try {
        const pendingFiles = sessionStorage.getItem('pendingUploadFiles');
        if (pendingFiles) {
          const filesMap = JSON.parse(pendingFiles);
          for (const [fieldId, files] of Object.entries(filesMap)) {
            if (formDataObj.answers && Array.isArray(files) && files.length > 0) {
              formDataObj.answers[fieldId] = files;
            }
          }
          sessionStorage.removeItem('pendingUploadFiles');
        }
      } catch (e) {
        console.warn('Error restaurando archivos:', e);
      }

      fetch(`${FORMS_BACKEND_URL}/api/payment-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentId: paymentId || 'unknown',
          formData: formDataObj
        })
      })
      .then(response => response.json())
      .then(result => {
        console.log('✅ Formulario enviado correctamente:', result);
        setFormSent(true);
        localStorage.removeItem('pendingFormData');
      })
      .catch(err => {
        console.error('❌ Error enviando formulario:', err);
        localStorage.removeItem('pendingFormData');
      });
    } else {
      console.log('ℹ️ No hay formulario pendiente (pago normal sin formulario)');
    }

    // Limpiar localStorage
    localStorage.removeItem('currentPayment');
  }, []);

  return (
    <div className="success-container">
      <div className="success-card">
        <div className="success-icon-container">
          <CheckCircle className="success-icon" />
        </div>
        <h1 className="success-title">¡Pago Exitoso!</h1>
        <p className="success-text">
          Tu pago ha sido procesado correctamente. Recibirás un correo de confirmación con los detalles.
        </p>
        {formSent && (
          <p className="form-sent" style={{
            fontSize: '14px',
            color: '#10b981',
            marginTop: '12px'
          }}>
            ✅ Formulario enviado correctamente
          </p>
        )}

        {bookingStatus === 'sending' && (
          <p style={{ fontSize: '14px', color: '#fcd34d', marginTop: '12px' }}>
            ⏳ Reservando tu cita…
          </p>
        )}
        {bookingStatus === 'success' && bookingDetails && (
          <div style={{
            background: 'rgba(74,222,128,0.1)',
            border: '1px solid rgba(74,222,128,0.3)',
            borderRadius: '10px',
            padding: '16px',
            marginTop: '16px',
            textAlign: 'left'
          }}>
            <p style={{ fontSize: '14px', color: '#86efac', margin: '0 0 8px 0', fontWeight: 600 }}>
              ✅ Cita reservada: {bookingDetails.slotLabel}
            </p>
            {bookingDetails.meetLink && (
              <p style={{ fontSize: '13px', margin: '6px 0', wordBreak: 'break-all' }}>
                <strong>Link videollamada:</strong>{' '}
                <a href={bookingDetails.meetLink} style={{ color: '#BC50DA' }} target="_blank" rel="noopener noreferrer">
                  {bookingDetails.meetLink}
                </a>
              </p>
            )}
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', margin: '6px 0 0 0' }}>
              Recibirás un email con el enlace y los detalles.
            </p>
          </div>
        )}
        {bookingStatus === 'error' && (
          <p style={{ fontSize: '14px', color: '#fca5a5', marginTop: '12px' }}>
            ⚠️ Hubo un problema reservando tu cita. Te contactaremos pronto.
          </p>
        )}
        {orderId && (
          <p className="order-id" style={{ 
            fontSize: '13px', 
            opacity: 0.7, 
            marginTop: '16px',
            fontFamily: 'monospace' 
          }}>
            Order ID: {orderId}
          </p>
        )}
        <button
          onClick={() => (window.location.href = 'https://eludoteca.org')}
          className="success-button"
        >
          <Home className="button-icon" />
          Volver al Inicio
        </button>
      </div>
    </div>
  );
}

export default Success;
